import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BellIcon, CheckIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/ApiService';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import type { BuildNotification, NotificationPrefs } from '../../types/buildExpansion';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';

const POLL_INTERVAL_MS = 90_000;

/** Route where NotificationPreferences is expected to be mounted by the wiring agent. */
export const NOTIFICATION_PREFERENCES_PATH = '/settings/notifications';

const timeAgo = (iso: string): string => {
  const then = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T')).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.floor((Date.now() - then) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(then).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

// ─── Panel ───────────────────────────────────────────────────────────────────

interface NotificationPanelProps {
  onClose: () => void;
  onUnreadChange: (unread: number) => void;
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({ onClose, onUnreadChange }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState<BuildNotification[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setUnreadEverywhere = useCallback(
    (value: number) => {
      const next = Math.max(0, value);
      setUnread(next);
      onUnreadChange(next);
    },
    [onUnreadChange]
  );

  const load = useCallback(
    async (targetPage: number, append: boolean) => {
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(null);
      try {
        const result = await apiService.getNotifications(targetPage);
        setItems((prev) => (append ? [...prev, ...result.items] : result.items));
        setPage(result.page || targetPage);
        setTotal(result.total);
        setUnreadEverywhere(result.unread);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Could not load notifications.');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [setUnreadEverywhere]
  );

  useEffect(() => {
    void load(1, false);
  }, [load]);

  const openNotification = async (notification: BuildNotification) => {
    if (!notification.is_read) {
      setItems((prev) => prev.map((n) => (n.id === notification.id ? { ...n, is_read: true } : n)));
      setUnreadEverywhere(unread - 1);
      try {
        await apiService.markNotificationsRead([notification.id]);
      } catch {
        // Non-blocking: leave the optimistic read state; next poll reconciles.
      }
    }
    if (notification.destination_url) {
      onClose();
      if (/^https?:\/\//i.test(notification.destination_url)) {
        window.location.assign(notification.destination_url);
      } else {
        navigate(notification.destination_url);
      }
    }
  };

  const markAllRead = async () => {
    setMarkingAll(true);
    const previous = items;
    const previousUnread = unread;
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnreadEverywhere(0);
    try {
      await apiService.markNotificationsRead();
    } catch (e: unknown) {
      setItems(previous);
      setUnreadEverywhere(previousUnread);
      setError(e instanceof Error ? e.message : 'Could not mark notifications as read.');
    } finally {
      setMarkingAll(false);
    }
  };

  const hasMore = items.length < total;

  return (
    <div
      className="absolute right-0 mt-2 w-[24rem] max-w-[calc(100vw-1.5rem)] rounded-xl border border-white/[0.08] bg-[#0a0a0a] shadow-elevated z-50 flex flex-col max-h-[min(28rem,70vh)]"
      role="region"
      aria-label="Notifications"
    >
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-white/[0.06] shrink-0">
        <h2 className="text-sm font-semibold text-white">
          Notifications
          {unread > 0 ? <span className="ml-2 text-xs font-medium text-orange-300">{unread} unread</span> : null}
        </h2>
        {unread > 0 && (
          <button
            type="button"
            onClick={markAllRead}
            disabled={markingAll}
            className="inline-flex items-center gap-1 text-xs font-medium text-gray-300 hover:text-white rounded-lg px-2 py-2 min-h-[36px] cursor-pointer disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50"
          >
            <CheckIcon className="w-4 h-4" aria-hidden />
            {markingAll ? 'Marking…' : 'Mark all read'}
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto overscroll-contain">
        {loading ? (
          <div className="flex items-center justify-center gap-3 py-10" role="status">
            <span className="spinner-sm" aria-hidden />
            <span className="text-sm text-gray-400">Loading notifications…</span>
          </div>
        ) : error ? (
          <div className="p-4">
            <div role="alert" className="alert-error mb-3">
              {error}
            </div>
            <Button type="button" variant="secondary" size="sm" onClick={() => void load(1, false)}>
              Try again
            </Button>
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            variant="dense"
            icon={<BellIcon className="w-10 h-10" />}
            title="You're all caught up"
            description="Important activity across events, builds, and clubs will show up here."
          />
        ) : (
          <ul className="divide-y divide-white/[0.06]/80">
            {items.map((notification) => (
              <li key={notification.id}>
                <button
                  type="button"
                  onClick={() => void openNotification(notification)}
                  className={`w-full text-left px-4 py-3 min-h-[44px] flex gap-3 cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-orange-500/50 ${
                    notification.is_read ? 'hover:bg-white/[0.04]' : 'bg-orange-500/[0.06] hover:bg-orange-500/10'
                  }`}
                >
                  <span
                    className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${
                      notification.is_read ? 'bg-transparent' : 'bg-orange-400'
                    }`}
                    aria-hidden
                  />
                  <span className="min-w-0 flex-1">
                    <span
                      className={`block text-sm truncate ${
                        notification.is_read ? 'text-gray-300' : 'text-white font-semibold'
                      }`}
                    >
                      {!notification.is_read && <span className="sr-only">Unread: </span>}
                      {notification.title}
                    </span>
                    {notification.body ? (
                      <span className="block text-xs text-gray-500 mt-0.5 line-clamp-2">{notification.body}</span>
                    ) : null}
                    <span className="block text-[11px] text-gray-500 mt-1">{timeAgo(notification.created_at)}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {!loading && !error && hasMore && (
          <div className="p-3 border-t border-white/[0.06]">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              fullWidth
              loading={loadingMore}
              loadingLabel="Loading…"
              onClick={() => void load(page + 1, true)}
            >
              Load more
            </Button>
          </div>
        )}
      </div>

      <div className="px-4 py-2.5 border-t border-white/[0.06] shrink-0 flex items-center justify-between gap-3">
        <Link
          to="/notifications"
          onClick={onClose}
          className="text-xs font-medium text-orange-300 hover:text-orange-200 inline-flex items-center min-h-[36px] rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50"
        >
          View all
        </Link>
        <Link
          to={NOTIFICATION_PREFERENCES_PATH}
          onClick={onClose}
          className="text-xs font-medium text-gray-400 hover:text-white inline-flex items-center min-h-[36px] rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50"
        >
          Preferences
        </Link>
      </div>
    </div>
  );
};

// ─── Bell ────────────────────────────────────────────────────────────────────

interface NotificationBellProps {
  className?: string;
}

/**
 * Header bell with unread badge. Polls unread notifications every 90s while
 * the user is authenticated and the tab is visible. Renders nothing signed out.
 */
export const NotificationBell: React.FC<NotificationBellProps> = ({ className = '' }) => {
  const { isAuthenticated } = useAuth();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      setUnread(0);
      setOpen(false);
      return undefined;
    }

    let timer: number | null = null;
    const poll = async () => {
      try {
        const result = await apiService.getNotifications(1, true);
        setUnread(result.unread);
      } catch {
        // Polling is best-effort; keep the last known count.
      }
    };
    const start = () => {
      if (timer !== null) return;
      void poll();
      timer = window.setInterval(() => void poll(), POLL_INTERVAL_MS);
    };
    const stop = () => {
      if (timer !== null) {
        window.clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') start();
      else stop();
    };

    onVisibility();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isAuthenticated]);

  // Close on outside click and Escape (with focus restoration)
  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!isAuthenticated) return null;

  const label =
    unread > 0
      ? `Notifications, ${unread} unread ${unread === 1 ? 'notification' : 'notifications'}`
      : 'Notifications, no unread notifications';

  return (
    <div className={`relative ${className}`.trim()} ref={wrapperRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="btn-icon relative min-h-[44px] min-w-[44px]"
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <BellIcon className="w-6 h-6" aria-hidden />
        {unread > 0 && (
          <span
            className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-orange-500 text-white text-[10px] font-bold flex items-center justify-center border border-[#0a0a0a]"
            aria-hidden
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && <NotificationPanel onClose={() => setOpen(false)} onUnreadChange={setUnread} />}
    </div>
  );
};

// ─── Preferences ─────────────────────────────────────────────────────────────

const ACTIVITY_LABELS: Record<string, string> = {
  modification_added: 'Major modifications',
  dyno_recorded: 'New dyno results',
  personal_best: 'Personal bests',
  award_won: 'Awards',
  achievement_earned: 'Achievements',
  featured_selected: 'Featured Build selections',
  album_added: 'New albums',
  build_status_changed: 'Build status changes',
  club_post: 'Club updates',
  event_post: 'Event updates',
};

const CHANNEL_LABELS: Record<string, string> = {
  in_app: 'In-app',
  email: 'Email',
  push: 'Mobile push',
  web_push: 'Browser notifications',
};

const CATEGORY_LABELS: Record<string, string> = {
  account_security: 'Account and security',
  builds: 'Builds',
  events: 'Events',
  organizer: 'Organizer activity',
  judging: 'Judging',
  clubs: 'Clubs',
  shops: 'Shops',
  passport: 'Passport',
  maintenance: 'Maintenance',
  premium: 'Premium',
  product_updates: 'Product updates',
  marketing: 'Marketing',
};

const EMAIL_CAT_LABELS: Record<string, { label: string; hint: string; locked?: boolean }> = {
  transactional: {
    label: 'Account and security',
    hint: 'Required for verification, password resets, and purchase receipts. Cannot be turned off.',
    locked: true,
  },
  product: {
    label: 'Product updates',
    hint: 'Optional product messages about features and watched-build activity when email is enabled.',
  },
  marketing: {
    label: 'Offers and promotions',
    hint: 'Optional marketing. Also controlled from Privacy and data.',
  },
};

const NESTED_KEYS = new Set(['channels', 'email', 'categories', 'preset']);

const prettifyPrefKey = (key: string): string =>
  key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

function isPlainObject(value: unknown): value is Record<string, boolean> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isCategoryMap(value: unknown): value is Record<string, Record<string, boolean>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Notification preference center: channels, email categories, and watched-build activity.
 * Nested `channels` / `email` objects are never flattened into boolean toggles.
 */
export const NotificationPreferences: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { showToast } = useToast();
  const [prefs, setPrefs] = useState<NotificationPrefs | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [browserStatus, setBrowserStatus] = useState<string>('checking');
  const [enablingBrowser, setEnablingBrowser] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.getNotificationPrefs();
      setPrefs(result);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not load notification preferences.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void (async () => {
      try {
        const { getBrowserPushStatus } = await import('../../lib/webPush');
        setBrowserStatus(await getBrowserPushStatus());
      } catch {
        setBrowserStatus('unsupported');
      }
    })();
  }, []);

  const persist = async (next: NotificationPrefs, key: string) => {
    const previous = prefs;
    setPrefs(next);
    setSavingKey(key);
    try {
      await apiService.setNotificationPrefs(next);
      showToast('Preferences saved.', 'success', { dedupeKey: 'notif-prefs-saved' });
    } catch (e: unknown) {
      setPrefs(previous);
      showToast(e instanceof Error ? e.message : 'Could not save preferences. Try again.', 'error');
    } finally {
      setSavingKey(null);
    }
  };

  const toggleActivity = async (key: string) => {
    if (!prefs || savingKey || typeof prefs[key] !== 'boolean') return;
    await persist({ ...prefs, [key]: !prefs[key] }, key);
  };

  const toggleChannel = async (key: string) => {
    if (!prefs || savingKey) return;
    const channels: Record<string, boolean> = isPlainObject(prefs.channels)
      ? { ...prefs.channels }
      : { in_app: true, email: false, push: true, web_push: true };
    channels[key] = !Boolean(channels[key]);
    await persist({ ...prefs, channels }, `channels.${key}`);
  };

  const toggleEmailCat = async (key: string) => {
    if (!prefs || savingKey || key === 'transactional') return;
    const email: Record<string, boolean> = isPlainObject(prefs.email)
      ? { ...prefs.email }
      : { transactional: true, product: false, marketing: false };
    email[key] = !Boolean(email[key]);
    email.transactional = true;
    await persist({ ...prefs, email }, `email.${key}`);
  };

  const toggleCategoryChannel = async (category: string, channel: string) => {
    if (!prefs || savingKey) return;
    const categories = isCategoryMap(prefs.categories) ? { ...prefs.categories } : {};
    const row = { ...(categories[category] || {}) };
    row[channel] = !Boolean(row[channel]);
    categories[category] = row;
    await persist({ ...prefs, categories }, `categories.${category}.${channel}`);
  };

  const enableBrowserPush = async () => {
    setEnablingBrowser(true);
    try {
      const { requestBrowserPushPermission, getBrowserPushStatus } = await import('../../lib/webPush');
      const result = await requestBrowserPushPermission();
      setBrowserStatus(await getBrowserPushStatus());
      if (result.ok) {
        showToast('Browser notifications enabled.', 'success');
        if (prefs) {
          const channels: Record<string, boolean> = isPlainObject(prefs.channels)
            ? { ...prefs.channels, web_push: true }
            : { in_app: true, email: false, push: true, web_push: true };
          await persist({ ...prefs, channels }, 'channels.web_push');
        }
      } else if (result.error) {
        showToast(result.error, 'error');
      }
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : 'Could not enable browser notifications.', 'error');
    } finally {
      setEnablingBrowser(false);
    }
  };

  const activityKeys = prefs
    ? Object.keys(prefs).filter((k) => !NESTED_KEYS.has(k) && typeof prefs[k] === 'boolean')
    : [];
  const channels = prefs && isPlainObject(prefs.channels) ? prefs.channels : null;
  const emailCats = prefs && isPlainObject(prefs.email) ? prefs.email : null;
  const categories = prefs && isCategoryMap(prefs.categories) ? prefs.categories : null;

  const SwitchRow = ({
    id,
    label,
    hint,
    enabled,
    locked,
    onToggle,
  }: {
    id: string;
    label: string;
    hint?: string;
    enabled: boolean;
    locked?: boolean;
    onToggle: () => void;
  }) => (
    <li className="flex items-start justify-between gap-4 py-2.5">
      <div className="min-w-0">
        <span id={id} className="text-sm text-gray-200">
          {label}
          {locked ? <span className="text-zinc-500"> (required)</span> : null}
        </span>
        {hint ? <p className="mt-0.5 text-xs text-zinc-500">{hint}</p> : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-labelledby={id}
        disabled={locked || savingKey !== null}
        onClick={onToggle}
        className={`toggle-track ${enabled ? 'toggle-track-on' : ''} shrink-0 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950`}
      >
        <span className={`toggle-thumb ${enabled ? 'toggle-thumb-on' : ''}`} aria-hidden />
        <span className="sr-only">{enabled ? 'On' : 'Off'}</span>
      </button>
    </li>
  );

  const browserHint =
    browserStatus === 'unsupported'
      ? 'This browser does not support Web Push.'
      : browserStatus === 'denied'
        ? 'Blocked in browser settings. Re-enable notifications for this site, then return here.'
        : browserStatus === 'subscribed'
          ? 'Enabled for this browser.'
          : 'Enable after you understand when Bilt:Spec will notify you (for example on an organizer dashboard).';

  return (
    <div className={`space-y-6 ${className}`.trim()}>
      <section className="card p-5" aria-labelledby="notif-channels-heading">
        <h2 id="notif-channels-heading" className="heading-card mb-1">
          Delivery channels
        </h2>
        <p className="text-sm text-gray-400 mb-4">
          Choose how optional updates reach you. Account and security email still sends when required.
        </p>
        {loading ? (
          <div className="space-y-3" role="status" aria-busy="true">
            <span className="sr-only">Loading preferences…</span>
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-11 rounded-lg bg-[#141414]/70 animate-pulse motion-reduce:animate-none" aria-hidden />
            ))}
          </div>
        ) : error ? (
          <div>
            <div role="alert" className="alert-error mb-3">
              {error}
            </div>
            <Button type="button" variant="secondary" size="sm" onClick={() => void load()}>
              Try again
            </Button>
          </div>
        ) : channels ? (
          <ul className="divide-y divide-white/[0.06]/80">
            {Object.keys(CHANNEL_LABELS).map((key) => (
              <SwitchRow
                key={key}
                id={`notif-channel-${key}`}
                label={CHANNEL_LABELS[key]}
                enabled={Boolean(channels[key])}
                onToggle={() => void toggleChannel(key)}
              />
            ))}
          </ul>
        ) : null}
        {!loading && !error && browserStatus !== 'unsupported' ? (
          <div className="mt-4 rounded-lg border border-white/10 bg-black/20 p-4">
            <p className="text-sm text-gray-200 mb-1">Browser notifications</p>
            <p className="text-xs text-zinc-500 mb-3">{browserHint}</p>
            {browserStatus !== 'subscribed' && browserStatus !== 'denied' ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={enablingBrowser}
                onClick={() => void enableBrowserPush()}
              >
                {enablingBrowser ? 'Enabling…' : 'Enable browser notifications'}
              </Button>
            ) : null}
          </div>
        ) : null}
      </section>

      <section className="card p-5" aria-labelledby="notif-email-heading">
        <h2 id="notif-email-heading" className="heading-card mb-1">
          Email categories
        </h2>
        <p className="text-sm text-gray-400 mb-4">
          Marketing stays separate from required transactional messages.
        </p>
        {emailCats && !loading && !error ? (
          <ul className="divide-y divide-white/[0.06]/80">
            {Object.keys(EMAIL_CAT_LABELS).map((key) => {
              const meta = EMAIL_CAT_LABELS[key];
              return (
                <SwitchRow
                  key={key}
                  id={`notif-email-${key}`}
                  label={meta.label}
                  hint={meta.hint}
                  enabled={key === 'transactional' ? true : Boolean(emailCats[key])}
                  locked={meta.locked}
                  onToggle={() => void toggleEmailCat(key)}
                />
              );
            })}
          </ul>
        ) : null}
      </section>

      {categories && !loading && !error ? (
        <section className="card p-5" aria-labelledby="notif-categories-heading">
          <h2 id="notif-categories-heading" className="heading-card mb-1">
            Categories
          </h2>
          <p className="text-sm text-gray-400 mb-4">
            Fine-tune optional push and email by topic. Required security messages stay on.
          </p>
          <ul className="space-y-4">
            {Object.keys(CATEGORY_LABELS)
              .filter((key) => categories[key])
              .map((key) => {
                const row = categories[key] || {};
                return (
                  <li key={key} className="rounded-lg border border-white/[0.06] p-3">
                    <p className="text-sm text-gray-200 mb-2">{CATEGORY_LABELS[key] ?? prettifyPrefKey(key)}</p>
                    <div className="flex flex-wrap gap-2">
                      {(['in_app', 'push', 'web_push', 'email'] as const).map((ch) => (
                        <button
                          key={ch}
                          type="button"
                          disabled={savingKey !== null || key === 'account_security'}
                          onClick={() => void toggleCategoryChannel(key, ch)}
                          className={`rounded-full px-3 py-1 text-xs border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50 ${
                            row[ch]
                              ? 'border-orange-500/40 bg-orange-500/15 text-orange-100'
                              : 'border-white/10 text-zinc-500'
                          }`}
                          aria-pressed={Boolean(row[ch])}
                        >
                          {CHANNEL_LABELS[ch] ?? ch}
                        </button>
                      ))}
                    </div>
                  </li>
                );
              })}
          </ul>
        </section>
      ) : null}

      <section className="card p-5" aria-labelledby="notification-prefs-heading">
        <h2 id="notification-prefs-heading" className="heading-card mb-1">
          Builds and activity
        </h2>
        <p className="text-sm text-gray-400 mb-5">
          Choose which updates you get from builds you watch, plus related club and event posts.
        </p>

        {!loading && !error && activityKeys.length === 0 ? (
          <EmptyState
            variant="dense"
            title="No preference options"
            description="Notification types will appear here once available."
          />
        ) : null}

        {!loading && !error && activityKeys.length > 0 ? (
          <ul className="divide-y divide-white/[0.06]/80">
            {activityKeys.map((key) => (
              <SwitchRow
                key={key}
                id={`notif-pref-label-${key}`}
                label={ACTIVITY_LABELS[key] ?? prettifyPrefKey(key)}
                enabled={Boolean(prefs?.[key])}
                onToggle={() => void toggleActivity(key)}
              />
            ))}
          </ul>
        ) : null}
      </section>
    </div>
  );
};

const NotificationCenter = { NotificationBell, NotificationPreferences };
export default NotificationCenter;
